import { useCallback, useState } from 'react';
import { TOOLBOX_ID_PREGUNTA_ITEM } from '../config/toolboxEvidencia';
import { crearEvento, dispararWebhook } from '../services/eventoEvaluacionService';
import api from '../services/api';
import { validarCierreEvidencia } from '../utils/validarCierreEvidencia';
import { dataUrlToFile } from '../utils/dataUrlToFile';

async function buscarOCrearEntidad({ nombre, legajo }) {
  const res = await api.get(
    `/api/evento_evaluacion/entidades?legajo=${encodeURIComponent(legajo)}`
  );
  const lista = Array.isArray(res.data) ? res.data : Array.isArray(res) ? res : [];
  if (lista.length > 0) return lista[0].id;

  const created = await api.post('/api/evento_evaluacion/entidades', {
    id_tipo_entidad: 1,
    nombre,
    metadata: { legajo },
  });
  return (created.data ?? created).id;
}

async function obtenerItemEvidenciaToolbox(id_evaluacion) {
  const itemsResp = await api.get(`/api/informes/items?evaluacion=${id_evaluacion}`);
  const itemsEvaluacion = itemsResp.data || [];
  const itemEvaluacion =
    itemsEvaluacion.find((item) => item.id_evaluacion === id_evaluacion) ||
    itemsEvaluacion[0];

  if (!itemEvaluacion) {
    throw new Error('No se encontró el item de evaluación');
  }

  const id_item_evaluacion = itemEvaluacion.id;
  const id_evidencia =
    itemEvaluacion?.Item_Evaluacion?.[0]?.Evidencias?.[0]?.id ?? null;

  return { id_item_evaluacion, id_evidencia };
}

function useGuardarToolbox() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const guardar = useCallback(
    async ({
      participantes,
      charla,
      horaInicio,
      horaFin,
      fecha,
      responsableId,
      tipoOperativo,
      lugarOperativo,
      firma,
      evidenciasFoto = [],
    }) => {
      setLoading(true);
      setError(null);

      try {
        // VC.7a — Validación de evidencia mínima (toolbox: mínimo 1 foto).
        // Guard temprano: falla ANTES de crear evento/informes para no dejar
        // registros huérfanos. La cantidad se conoce del input (MVP: 1 foto).
        const fotosAdjuntas = evidenciasFoto[0]?.file ? 1 : 0;
        const valEvidencia = validarCierreEvidencia('toolbox', {
          fotos: fotosAdjuntas,
          videos: 0,
        });
        if (!valEvidencia.ok) {
          const errEvidencia = new Error(valEvidencia.mensaje);
          setError(errEvidencia);
          return { success: false, evidenciaError: true, error: errEvidencia };
        }

        const entidadIds = await Promise.all(participantes.map(buscarOCrearEntidad));

        const eventoRes = await crearEvento({
          nombre: `Toolbox - ${charla}`,
          descripcion: charla,
          fecha_programada: fecha,
          origen: 'toolbox',
          id_plantilla: 4,
          metadata: { horaInicio, horaFin, charla, tipoOperativo, lugarOperativo },
        });
        const id_evento = eventoRes.data?.id;
        if (!id_evento) throw new Error('No se pudo crear el evento de toolbox');

        const primerWebhook = await dispararWebhook({
          id_evento,
          id_evaluado: entidadIds[0],
        });
        const primerIdInforme = primerWebhook.data?.id_informe;
        const primerIdEvaluacion = primerWebhook.data?.id_evaluacion;
        if (!primerIdInforme) {
          throw new Error('No se pudo crear el informe del participante');
        }

        const informeIds = [primerIdInforme];
        const evidenciaFile = evidenciasFoto[0]?.file;

        if (evidenciaFile) {
          try {
            const { id_item_evaluacion, id_evidencia } =
              await obtenerItemEvidenciaToolbox(primerIdEvaluacion);

            if (!id_evidencia || !id_item_evaluacion) {
              throw new Error('No hay slot de evidencia disponible para este toolbox');
            }

            await api.uploadEvidencia(
              id_item_evaluacion,
              TOOLBOX_ID_PREGUNTA_ITEM,
              id_evidencia,
              evidenciaFile
            );
          } catch (uploadErr) {
            setError(uploadErr);
            return { success: false, evidenciaError: true, error: uploadErr };
          }
        }

        if (entidadIds.length > 1) {
          const restantes = await Promise.all(
            entidadIds.slice(1).map(async (id_entidad) => {
              const webhookRes = await dispararWebhook({
                id_evento,
                id_evaluado: id_entidad,
              });
              const id_informe = webhookRes.data?.id_informe;
              if (!id_informe) {
                throw new Error('No se pudo crear el informe del participante');
              }
              return id_informe;
            })
          );
          informeIds.push(...restantes);
        }

        await Promise.all(
          informeIds.map((id_informe) =>
            api.put(`/api/informes/${id_informe}`, { id_estado_informe: 4 })
          )
        );

        let firmaError = false;
        const firmaFile = dataUrlToFile(firma);
        if (firmaFile) {
          await Promise.all(
            informeIds.map((id_informe) =>
              api.uploadFirma(id_informe, firmaFile).catch((err) => {
                console.warn(
                  `[FIRMA] Error subiendo firma toolbox informe ${id_informe}:`,
                  err.message
                );
                firmaError = true;
              })
            )
          );
        }

        return { success: true, id_evento, firmaError, evidenciaError: false };
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { guardar, loading, error };
}

export default useGuardarToolbox;
